import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Socket, io } from "socket.io-client";
import type { BanDurationType } from "@shared/otherTypes";
import { ListenEventsMap } from "@shared/socketioTypes";


/**
 * clsx then tailwind-merge
 */
export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}


/**
 * Removes the indentation of multiline strings based on the minimum length of indentation
 */
export const stripIndent = (src: string) => {
    const indentSearchRegex = /^[ \t]*(?=\S)/gm;
    const indents = src.substring(src.indexOf('\n')).match(indentSearchRegex);
    if (!indents) return src;
    const minIndent = indents.reduce((r, a) => Math.min(r, a.length), Infinity);
    const indentRemoverRegex = new RegExp(`^[ \\t]{${minIndent}}`, 'gm');
    return src.replace(indentRemoverRegex, '');
};


/**
 * Converts a number to a locale string with commas and decimals
 */
export const numberToLocaleString = (num: number, decimals = 0) => {
    return num.toLocaleString(
        window.txBrowserLocale,
        { maximumFractionDigits: decimals }
    );
};


// ── Mock Socket.IO Connection Client for DEV Mode ─────────────────────────────
class MockSocket {
    private callbacks: Record<string, Function[]> = {};
    private intervals: any[] = [];
    private isDisconnected = false;

    constructor(rooms: string[] | string) {
        console.log('[MOCK SOCKET INIT] Connecting to rooms:', rooms);
        
        setTimeout(() => {
            if (this.isDisconnected) return;
            this.trigger('connect');

            // Send initial status and playerlist
            import('./mock').then(({ MOCK_GLOBAL_STATUS, MOCK_PLAYERS }) => {
                this.trigger('status', MOCK_GLOBAL_STATUS);
                this.trigger('playerlist', [
                    { type: 'fullPlayerlist', mutex: 'mock-mutex', playerlist: MOCK_PLAYERS }
                ]);
            });

            // Start status & playerlist loop
            const statusInterval = setInterval(() => {
                if (this.isDisconnected) return;
                import('./mock').then(({ MOCK_GLOBAL_STATUS, MOCK_PLAYERS }) => {
                    const status = { ...MOCK_GLOBAL_STATUS };
                    status.server.uptime += 5;
                    this.trigger('status', status);

                    // Periodically trigger mock join/drop events
                    if (Math.random() > 0.7) {
                        const randomPlayer = MOCK_PLAYERS[Math.floor(Math.random() * MOCK_PLAYERS.length)];
                        if (Math.random() > 0.5) {
                            this.trigger('playerlist', [
                                { type: 'playerJoining', mutex: 'mock-mutex', ...randomPlayer, netid: Math.floor(Math.random() * 1000) }
                            ]);
                        } else {
                            this.trigger('playerlist', [
                                { type: 'playerDropped', mutex: 'mock-mutex', netid: randomPlayer.netid, reasonCategory: 'Timeout' }
                            ]);
                        }
                    }
                });
            }, 5000);
            this.intervals.push(statusInterval);

            // Start console logs loop
            const logLines = [
                'hb received from FXServer',
                'server thread hitch warning: timer interval of 152 milliseconds',
                '[script:ox_lib] Executed callback success',
                '[script:es_extended] Player list synchronizer pinged',
                'heartbeat sent successfully',
            ];
            const consoleInterval = setInterval(() => {
                if (this.isDisconnected) return;
                const randomLine = logLines[Math.floor(Math.random() * logLines.length)];
                const formattedLine = `\x1b[32m[${new Date().toLocaleTimeString()}]\x1b[0m ${randomLine}`;
                this.trigger('consoleData', formattedLine + '\r\n');
            }, 3000);
            this.intervals.push(consoleInterval);

            // Start dashboard details loop
            const dashInterval = setInterval(() => {
                if (this.isDisconnected) return;
                this.trigger('dashboard', {
                    svRuntime: {
                        fxsMemory: 4096 + Math.floor(Math.random() * 1024),
                        nodeMemory: { rss: 250, heapTotal: 180, heapUsed: 120, external: 10 },
                        perfBucketCounts: {
                            svMain: [1, 2, 4, 8, 12, 10, 5, 2],
                            svNetwork: [0, 1, 2, 5, 15, 8, 2, 0],
                        }
                    },
                    playerDrop: {
                        summaryLast6h: [
                            ['Crash', 12],
                            ['Timeout', 8],
                            ['Kick', 4],
                            ['Quit', 24],
                        ]
                    }
                });
            }, 4000);
            this.intervals.push(dashInterval);

        }, 150);
    }

    on(event: string, callback: Function) {
        if (!this.callbacks[event]) {
            this.callbacks[event] = [];
        }
        this.callbacks[event].push(callback);
        return this;
    }

    emit(event: string, ...args: any[]) {
        console.log('[MOCK SOCKET EMIT]', event, args);
        if (event === 'consoleCommand') {
            const cmd = args[0];
            setTimeout(() => {
                this.trigger('consoleData', `\r\n\x1b[33mExecuting simulated command: ${cmd}\x1b[0m\r\n`);
                this.trigger('consoleData', `\x1b[32mCommand "${cmd}" completed with exit code 0.\x1b[0m\r\n`);
            }, 300);
        }
        return this;
    }

    disconnect() {
        this.isDisconnected = true;
        for (const interval of this.intervals) {
            clearInterval(interval);
        }
        this.trigger('disconnect', 'io client disconnect');
        return this;
    }

    removeAllListeners() {
        this.callbacks = {};
        return this;
    }

    private trigger(event: string, ...args: any[]) {
        const list = this.callbacks[event];
        if (list) {
            for (const cb of list) {
                cb(...args);
            }
        }
    }
}

/**
 * Returns a socket.io client instance
 */
export const getSocket = (rooms: string[] | string) => {
    if (import.meta.env.DEV) {
        return new MockSocket(rooms) as unknown as Socket<ListenEventsMap, any>;
    }

    const socketOpts = {
        transports: ['polling'],
        upgrade: false,
        query: {
            rooms,
            uiVersion: window.txConsts.txaVersion,
        }
    };

    const socket = window.txConsts.isWebInterface
        ? io({ ...socketOpts, path: '/socket.io' })
        : io('monitor', { ...socketOpts, path: '/WebPipe/socket.io' });

    //Can't use the generic type on io(), so need to apply it here
    return socket as Socket<ListenEventsMap, any>;
}


/**
 * Returns a random hsl() color - useful for testing react rendering stuff
 */
export const createRandomHslColor = (alpha?: number) => {
    const hue = Math.floor(Math.random() * 360);
    return typeof alpha === 'number'
        ? `hsla(${hue}, 100%, 50%, ${alpha})`
        : `hsl(${hue}, 100%, 50%)`
}


/**
 * Returns a deterministic hsl() color based on a seed string
 */
export const createSeedHslColor = (seed: string, alpha?: number) => {
    const hash = seed.split('').reduce((acc, char) => {
        return ((acc << 5) - acc) + char.charCodeAt(0) | 0;
    }, 0);
    const hue = Math.abs(hash % 360);
    return typeof alpha === 'number'
        ? `hsla(${hue}, 100%, 50%, ${alpha})`
        : `hsl(${hue}, 100%, 50%)`;
}


/**
 * Copy text to clipboard.
 * Only if on web, attempt to use the Clipboard API. If it fails, fallback to the old method.
 * Because we don't have access to Clipboard API in FiveM's CEF, as well as on
 * non-localhost origins without https, we need to use the old school method.
 * It does seem to work on Firefox though.
 */
export const copyToClipboard = async (
    value: string,
    surrogate: HTMLDivElement,
    returnFocusTo: HTMLElement | null = null,
) => {
    const copyViaApi = () => navigator.clipboard.writeText(value);
    const copyViaInput = () => {
        const clipElem = document.createElement("textarea");
        clipElem.id = 'clipboard-input-' + Math.random().toString(36).substring(2, 15);
        clipElem.value = value;
        clipElem.style.position = 'fixed';
        clipElem.style.opacity = '0';
        clipElem.style.top = '0';
        clipElem.style.left = '0';
        surrogate.appendChild(clipElem);
        clipElem.select();
        const result = document.execCommand("copy");
        document.addEventListener('copy', () => {
            setTimeout(() => {
                try {
                    surrogate.removeChild(clipElem);
                    console.log('Removed clipboard temporary target:', clipElem.id);
                } catch (error) {
                    console.log('Failed to remove clipboard temporary target:', clipElem.id);
                } finally {
                    if (returnFocusTo) returnFocusTo.focus();
                };
            }, 0);
        }, {
            once: true,
            signal: AbortSignal.timeout(250)
        });
        return result;
    }

    //try to prevent printing error on devtools
    if (window.txConsts.isWebInterface && navigator.clipboard) {
        try {
            return await copyViaApi();
        } catch (error1) {
            return copyViaInput();
        }
    } else {
        console.warn('Clipboard API not available, copying via textarea.');
        return copyViaInput();
    }
}


/**
 * Converts the duration object to a lowercase string with correct unit pluralization
 */
export const banDurationToString = (duration: BanDurationType) => {
    if (duration === 'permanent') return 'permanent';
    if (typeof duration === 'string') return duration;
    const pluralizedString = duration.value === 1 ? duration.unit.slice(0, -1) : duration.unit;
    return `${duration.value} ${pluralizedString}`;
}


/**
 * Converts the duration object to a short string
 */
export const banDurationToShortString = (duration: BanDurationType) => {
    if (typeof duration === 'string') {
        return duration === 'permanent' ? 'PERM' : duration;
    }

    let suffix: string;
    if (duration.unit === 'hours') {
        suffix = 'h';
    } else if (duration.unit === 'days') {
        suffix = 'd';
    } else if (duration.unit === 'weeks') {
        suffix = 'w';
    } else if (duration.unit === 'months') {
        suffix = 'mo';
    } else {
        suffix = duration.unit;
    }
    return `${duration.value}${suffix}`;
}
